"use client";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  ArrowLeft,
  Phone,
  Mail,
  Target,
  Shield,
  CheckCircle,
  Award,
  Users,
} from "lucide-react";
import { competencyData, competencyCountries } from "../data/courses";

const CompetencyTraining = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/20">
      {/* Header */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/maritime-competency-training.png')] bg-cover bg-center"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent"></div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/courses"
              className="mb-6 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-blue-200 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:text-white"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Categories
            </Link>
            <br />

            <h1 className="mb-4 text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
              Competency Training
            </h1>
            <p className="max-w-3xl text-xl leading-relaxed text-slate-300">
              Professional maritime competency certifications for career
              advancement across multiple jurisdictions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <h2 className="mb-4 text-3xl font-bold text-slate-900 sm:text-4xl">
              Competency Training Fees
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-600">
              Choose your preferred jurisdiction for maritime competency
              certification. All fees are in USD.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-teal-600 to-teal-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">
                      RANK
                    </th>
                    {competencyCountries.map((country) => (
                      <th
                        key={country.key}
                        className="px-6 py-4 text-center text-sm font-semibold text-white"
                      >
                        {country.name}
                      </th>
                    ))}
                    <th className="px-6 py-4 text-center text-sm font-semibold text-white">
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {competencyData.map((row, index) => (
                    <tr
                      key={row.rank}
                      className={index % 2 === 0 ? "bg-slate-50" : "bg-white"}
                    >
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {row.rank}
                      </td>
                      {competencyCountries.map((country) => (
                        <td
                          key={country.key}
                          className="px-6 py-4 text-center text-sm text-slate-700"
                        >
                          ${row[country.key as keyof typeof row]}/USD
                        </td>
                      ))}
                      <td className="px-6 py-4 text-center">
                        <Link
                          to={`/payment?training=${encodeURIComponent("competency-training")}&course=${encodeURIComponent(row.rank)}`}
                          className="inline-flex items-center rounded-lg bg-gradient-to-r from-teal-600 to-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:scale-105 hover:from-teal-700 hover:to-teal-800 hover:shadow-lg"
                        >
                          <CheckCircle className="mr-1 h-4 w-4" />
                          Enroll
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 rounded-xl bg-gradient-to-r from-blue-50 to-teal-50 p-6"
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100">
                  <svg
                    className="h-4 w-4 text-teal-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              <div>
                <h4 className="mb-1 text-sm font-semibold text-slate-900">
                  Important Note
                </h4>
                <p className="text-sm text-slate-600">
                  Fees may vary based on specific requirements and
                  documentation. Contact our admissions team for detailed
                  pricing and course availability in your preferred
                  jurisdiction.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="bg-slate-900 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">
              Why Choose Our Competency Training?
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-slate-300">
              Professional development programs designed for maritime career
              advancement
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "International Recognition",
                description:
                  "Certificates recognized globally by maritime authorities",
                icon: Award,
              },
              {
                title: "Expert Training",
                description:
                  "Industry professionals with extensive sea experience",
                icon: Users,
              },
              {
                title: "Career Advancement",
                description:
                  "Enhanced opportunities for senior maritime positions",
                icon: Target,
              },
              {
                title: "Flexible Programs",
                description:
                  "Multiple country options to suit your career goals",
                icon: Shield,
              },
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                  <benefit.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="leading-relaxed text-slate-300">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-gradient-to-br from-teal-600 via-blue-600 to-cyan-600 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="mb-4 text-3xl font-bold text-white sm:text-4xl">
                Ready to Advance Your Career?
              </h2>
              <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-blue-100">
                Contact our competency training specialists to discuss your
                career goals and find the right certification path for your
                maritime advancement.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <a
                  href="tel:+918427775865"
                  className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-4 text-lg font-semibold text-teal-600 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-slate-100 hover:shadow-xl"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call Admissions
                </a>
                <a
                  href="mailto:info@nautilustimes.com"
                  className="inline-flex items-center justify-center rounded-xl border-2 border-white/20 bg-white/10 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:bg-white/20"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Email Inquiry
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompetencyTraining;
